package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TimePicker
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.theme.accentBlue
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.back_icon_cd
import rainbowclubs.composeapp.generated.resources.calendar_icon_cd
import rainbowclubs.composeapp.generated.resources.cancel
import rainbowclubs.composeapp.generated.resources.event_date_label
import rainbowclubs.composeapp.generated.resources.event_date_placeholder
import rainbowclubs.composeapp.generated.resources.event_description_icon_cd
import rainbowclubs.composeapp.generated.resources.event_description_label
import rainbowclubs.composeapp.generated.resources.event_description_placeholder
import rainbowclubs.composeapp.generated.resources.event_end_date_icon_cd
import rainbowclubs.composeapp.generated.resources.event_end_date_label
import rainbowclubs.composeapp.generated.resources.event_end_time_icon_cd
import rainbowclubs.composeapp.generated.resources.event_end_time_label
import rainbowclubs.composeapp.generated.resources.event_location_icon_cd
import rainbowclubs.composeapp.generated.resources.event_location_label
import rainbowclubs.composeapp.generated.resources.event_location_placeholder
import rainbowclubs.composeapp.generated.resources.event_schedule
import rainbowclubs.composeapp.generated.resources.event_schedule_icon_cd
import rainbowclubs.composeapp.generated.resources.event_start_time_icon_cd
import rainbowclubs.composeapp.generated.resources.event_start_time_label
import rainbowclubs.composeapp.generated.resources.event_time_placeholder
import rainbowclubs.composeapp.generated.resources.event_title_icon_cd
import rainbowclubs.composeapp.generated.resources.event_title_label
import rainbowclubs.composeapp.generated.resources.event_title_placeholder
import rainbowclubs.composeapp.generated.resources.ic_arrow_back
import rainbowclubs.composeapp.generated.resources.ic_calendar
import rainbowclubs.composeapp.generated.resources.ic_clock
import rainbowclubs.composeapp.generated.resources.ic_dashboard_grid
import rainbowclubs.composeapp.generated.resources.ic_document_edit
import rainbowclubs.composeapp.generated.resources.ic_location
import rainbowclubs.composeapp.generated.resources.ic_warning
import rainbowclubs.composeapp.generated.resources.ok
import rainbowclubs.composeapp.generated.resources.optional_label
import rainbowclubs.composeapp.generated.resources.warning_icon_cd

@Composable
fun EventFormHeader(
    title: StringResource,
    description: StringResource,
    icon: DrawableResource,
    iconContentDescription: StringResource,
    onBackClick: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        IconButton(
            onClick = onBackClick,
            modifier = Modifier.align(Alignment.Top).size(24.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(16.dp))

        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = stringResource(title),
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(modifier = Modifier.width(10.dp))
                Icon(
                    painter = painterResource(icon),
                    contentDescription = stringResource(iconContentDescription),
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.primary,
                )
            }

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = stringResource(description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
fun EventFormSection(
    title: StringResource,
    iconContentDescription: StringResource,
    accent: Color = MaterialTheme.colorScheme.primary,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            painter = painterResource(Res.drawable.ic_dashboard_grid),
            contentDescription = stringResource(iconContentDescription),
            modifier = Modifier.size(24.dp),
            tint = accent,
        )
        Spacer(modifier = Modifier.width(16.dp))
        Text(
            text = stringResource(title),
            color = MaterialTheme.colorScheme.onSurface,
            style = MaterialTheme.typography.titleLarge,
        )
    }
}

@Composable
fun EventFormFields(
    form: EventFormUiState,
    onTitleChanged: (String) -> Unit,
    onDescriptionChanged: (String) -> Unit,
    onLocationChanged: (String) -> Unit,
    onDateChanged: (String) -> Unit,
    onEndDateChanged: (String) -> Unit,
    onStartsAtChanged: (String) -> Unit,
    onEndsAtChanged: (String) -> Unit,
) {
    EventTextField(
        value = form.title,
        onValueChange = onTitleChanged,
        label = Res.string.event_title_label,
        placeholder = Res.string.event_title_placeholder,
        icon = Res.drawable.ic_document_edit,
        iconContentDescription = Res.string.event_title_icon_cd,
        error = form.titleError,
    )

    Spacer(modifier = Modifier.height(16.dp))

    EventTextField(
        value = form.description,
        onValueChange = onDescriptionChanged,
        label = Res.string.event_description_label,
        placeholder = Res.string.event_description_placeholder,
        icon = Res.drawable.ic_document_edit,
        iconContentDescription = Res.string.event_description_icon_cd,
        error = form.descriptionError,
        minLines = 3,
    )

    Spacer(modifier = Modifier.height(16.dp))

    EventTextField(
        value = form.location,
        onValueChange = onLocationChanged,
        label = Res.string.event_location_label,
        placeholder = Res.string.event_location_placeholder,
        icon = Res.drawable.ic_location,
        iconContentDescription = Res.string.event_location_icon_cd,
        optional = true,
    )

    Spacer(modifier = Modifier.height(34.dp))

    EventFormSection(
        title = Res.string.event_schedule,
        iconContentDescription = Res.string.event_schedule_icon_cd,
        accent = MaterialTheme.colorScheme.accentBlue,
    )

    Spacer(modifier = Modifier.height(24.dp))

    Row(
        horizontalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        EventDatePickerField(
            value = form.date,
            onValueChange = onDateChanged,
            label = Res.string.event_date_label,
            iconContentDescription = Res.string.calendar_icon_cd,
            error = form.dateError,
            modifier = Modifier.weight(1f),
        )
        EventDatePickerField(
            value = form.endDate,
            onValueChange = onEndDateChanged,
            label = Res.string.event_end_date_label,
            iconContentDescription = Res.string.event_end_date_icon_cd,
            error = form.endDateError,
            modifier = Modifier.weight(1f),
        )
    }

    Spacer(modifier = Modifier.height(16.dp))

    Row(
        horizontalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        EventTimePickerField(
            value = form.startsAt,
            onValueChange = onStartsAtChanged,
            label = Res.string.event_start_time_label,
            iconContentDescription = Res.string.event_start_time_icon_cd,
            error = form.startsAtError,
            modifier = Modifier.weight(1f),
        )
        EventTimePickerField(
            value = form.endsAt,
            onValueChange = onEndsAtChanged,
            label = Res.string.event_end_time_label,
            iconContentDescription = Res.string.event_end_time_icon_cd,
            error = form.endsAtError,
            modifier = Modifier.weight(1f),
        )
    }
}

@Composable
fun EventValidationError(error: StringResource?) {
    error ?: return

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.42f),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.28f)),
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(10.dp)
                    .background(MaterialTheme.colorScheme.error, RoundedCornerShape(10.dp)),
            )
            Spacer(modifier = Modifier.width(12.dp))
            Text(
                text = stringResource(error),
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.titleMedium,
            )
        }
    }
}

@Composable
fun EventSubmitButton(
    text: StringResource,
    isSubmitting: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    containerColor: Color = MaterialTheme.colorScheme.primary,
) {
    Button(
        onClick = onClick,
        enabled = !isSubmitting,
        modifier = modifier
            .fillMaxWidth()
            .height(56.dp),
        shape = RoundedCornerShape(20.dp),
        colors = ButtonDefaults.buttonColors(containerColor = containerColor),
    ) {
        if (isSubmitting) {
            CircularProgressIndicator(
                modifier = Modifier.size(22.dp),
                strokeWidth = 2.dp,
                color = MaterialTheme.colorScheme.onPrimary,
            )
        } else {
            Text(
                text = stringResource(text),
                style = MaterialTheme.typography.titleLarge,
            )
        }
    }
}

@Composable
fun FormErrorText(error: StringResource?) {
    error ?: return
    Text(
        text = stringResource(error),
        color = MaterialTheme.colorScheme.error,
        style = MaterialTheme.typography.titleMedium,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun EventDatePickerField(
    value: String,
    onValueChange: (String) -> Unit,
    label: StringResource,
    iconContentDescription: StringResource,
    modifier: Modifier = Modifier,
    error: StringResource? = null,
) {
    var isDialogOpen by remember { mutableStateOf(false) }
    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = eventDateToEpochMillis(value),
    )

    Box(modifier = modifier.fillMaxWidth()) {
        OutlinedTextField(
            value = value,
            onValueChange = {},
            readOnly = true,
            label = { Text(stringResource(label)) },
            placeholder = { Text(stringResource(Res.string.event_date_placeholder)) },
            leadingIcon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_calendar),
                    contentDescription = stringResource(iconContentDescription),
                    tint = MaterialTheme.colorScheme.primary,
                )
            },
            trailingIcon = if (error != null) {
                {
                    Icon(
                        painter = painterResource(Res.drawable.ic_warning),
                        contentDescription = stringResource(Res.string.warning_icon_cd),
                        tint = MaterialTheme.colorScheme.error,
                    )
                }
            } else {
                null
            },
            supportingText = if (error != null) {
                { Text(stringResource(error)) }
            } else {
                null
            },
            isError = error != null,
            textStyle = MaterialTheme.typography.titleMedium,
            modifier = Modifier.fillMaxWidth(),
        )
        Box(
            modifier = Modifier
                .matchParentSize()
                .clickable { isDialogOpen = true },
        )
    }

    if (isDialogOpen) {
        DatePickerDialog(
            onDismissRequest = { isDialogOpen = false },
            confirmButton = {
                TextButton(
                    onClick = {
                        datePickerState.selectedDateMillis?.let {
                            onValueChange(formatEventDate(it))
                        }
                        isDialogOpen = false
                    }
                ) {
                    Text(stringResource(Res.string.ok))
                }
            },
            dismissButton = {
                TextButton(onClick = { isDialogOpen = false }) {
                    Text(stringResource(Res.string.cancel))
                }
            },
        ) {
            DatePicker(state = datePickerState)
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun EventTimePickerField(
    value: String,
    onValueChange: (String) -> Unit,
    label: StringResource,
    iconContentDescription: StringResource,
    modifier: Modifier = Modifier,
    error: StringResource? = null,
) {
    var isDialogOpen by remember { mutableStateOf(false) }
    val selectedTime = parseEventTime(value)
    val timePickerState = rememberTimePickerState(
        initialHour = selectedTime?.hour ?: 17,
        initialMinute = selectedTime?.minute ?: 0,
        is24Hour = true,
    )

    Box(modifier = modifier.fillMaxWidth()) {
        OutlinedTextField(
            value = value,
            onValueChange = {},
            readOnly = true,
            label = { Text(stringResource(label)) },
            placeholder = { Text(stringResource(Res.string.event_time_placeholder)) },
            leadingIcon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_clock),
                    contentDescription = stringResource(iconContentDescription),
                    tint = MaterialTheme.colorScheme.primary,
                )
            },
            trailingIcon = if (error != null) {
                {
                    Icon(
                        painter = painterResource(Res.drawable.ic_warning),
                        contentDescription = stringResource(Res.string.warning_icon_cd),
                        tint = MaterialTheme.colorScheme.error,
                    )
                }
            } else {
                null
            },
            supportingText = if (error != null) {
                { Text(stringResource(error)) }
            } else {
                null
            },
            isError = error != null,
            textStyle = MaterialTheme.typography.titleMedium,
            modifier = Modifier.fillMaxWidth(),
        )
        Box(
            modifier = Modifier
                .matchParentSize()
                .clickable { isDialogOpen = true },
        )
    }

    if (isDialogOpen) {
        AlertDialog(
            onDismissRequest = { isDialogOpen = false },
            confirmButton = {
                TextButton(
                    onClick = {
                        onValueChange(
                            "${timePickerState.hour.toString().padStart(2, '0')}:${
                                timePickerState.minute.toString().padStart(2, '0')
                            }"
                        )
                        isDialogOpen = false
                    }
                ) {
                    Text(stringResource(Res.string.ok))
                }
            },
            dismissButton = {
                TextButton(onClick = { isDialogOpen = false }) {
                    Text(stringResource(Res.string.cancel))
                }
            },
            text = {
                TimePicker(state = timePickerState)
            },
        )
    }
}

@Composable
private fun EventTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: StringResource,
    placeholder: StringResource,
    icon: DrawableResource,
    iconContentDescription: StringResource,
    modifier: Modifier = Modifier,
    optional: Boolean = false,
    error: StringResource? = null,
    minLines: Int = 1,
    keyboardType: KeyboardType = KeyboardType.Text,
) {
    val labelText = if (optional) {
        "${stringResource(label)} ${stringResource(Res.string.optional_label)}"
    } else {
        stringResource(label)
    }

    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { Text(labelText) },
        placeholder = { Text(stringResource(placeholder)) },
        leadingIcon = {
            Icon(
                painter = painterResource(icon),
                contentDescription = stringResource(iconContentDescription),
                tint = MaterialTheme.colorScheme.primary,
            )
        },
        trailingIcon = if (error != null) {
            {
                Icon(
                    painter = painterResource(Res.drawable.ic_warning),
                    contentDescription = stringResource(Res.string.warning_icon_cd),
                    tint = MaterialTheme.colorScheme.error,
                )
            }
        } else {
            null
        },
        supportingText = if (error != null) {
            { Text(stringResource(error)) }
        } else {
            null
        },
        isError = error != null,
        minLines = minLines,
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
        textStyle = MaterialTheme.typography.titleMedium,
        modifier = modifier.fillMaxWidth(),
    )
}
